import React, { FC } from 'react';
import Image from 'next/image';
import completeIcon from '@/public/assets/icons/radial-progress-bar-complete.svg';
import {
  PROGRESS_BAR_COLOR_CLASSES_MAP,
  PROGRESS_BAR_TAILWIND_CLASSES,
} from './styles';

type ProgressBarProps = {
  percentage: number;
  styleVariant?: 'radial';
  colorVariant?: keyof typeof PROGRESS_BAR_COLOR_CLASSES_MAP;
};

const ProgressBar: FC<ProgressBarProps> = ({
  percentage,
  styleVariant,
  colorVariant = 'default',
}) => {
  const validatedPercentage = _getValidatePercentage(percentage);
  const isComplete = validatedPercentage === 100;

  const {
    filledCssClass,
    unfilledCssClass,
    filledTailwindClass,
    unfilledTailwindClass,
  } = PROGRESS_BAR_COLOR_CLASSES_MAP[colorVariant];

  if (styleVariant === 'radial') {
    if (isComplete) {
      return (
        <Image
          src={completeIcon}
          alt="complete"
          height={16}
          width={16}
          data-testid="radial-progress-bar-complete"
        />
      );
    }

    return (
      <div
        data-testid="radial-progress-bar"
        className="h-4 w-4 rounded-[50%]"
        style={{
          background: `radial-gradient(closest-side, white 74%, transparent 75% 100%), conic-gradient(${filledCssClass} ${validatedPercentage}%, ${unfilledCssClass} 0)`,
        }}
      />
    );
  }

  return (
    <div
      className={`h-1 w-full rounded-full ${unfilledTailwindClass}`}
      data-testid="progress-bar-container"
    >
      <div
        className={`h-1 ${PROGRESS_BAR_TAILWIND_CLASSES[validatedPercentage]} rounded-full ${filledTailwindClass} transition-width duration-700 ease-in`}
        data-testid="progress-bar"
      />
    </div>
  );
};

function _getValidatePercentage(percentage: number) {
  if (percentage < 0) {
    return 0;
  }

  if (percentage > 100) {
    return 100;
  }

  return percentage;
}

export default React.memo(ProgressBar);
